import { Box, Container, Divider, Grid, Typography } from "@mui/material";
import { motion, useScroll, useTransform } from "framer-motion";
import React, { useRef } from "react";
import CustomTextButton from "../Common/CustomButton";
import { COLORS } from '../../Constants/styles';
import {
  SportsBaseballOutlined,
  SportsBasketballOutlined,
  SportsFootballOutlined,
  SportsSoccerOutlined,
  SportsVolleyballOutlined
} from '@mui/icons-material';
import { Home_IMG } from "../../Constants/ImageConstants";

const BackgroundVideoSection: React.FC = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const rotate = useTransform(scrollYProgress, [0, 1], [-45, 45]);


  const values = ["Child-Centered.", "Excellence.", "Integrity.", "Passion.", "Teamwork.", "Inclusivity."];
  const sportsIcons = [
    SportsBaseballOutlined,
    SportsSoccerOutlined,
    SportsBasketballOutlined,
    SportsFootballOutlined,
    SportsVolleyballOutlined
  ];

  return (
    <>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: { xs: "22vh", md: "50vh" },
          paddingBottom: "10vh",
          width: "100%",
          background: `url(${Home_IMG.BackgroundVideoSection1}) center center / cover no-repeat`,
          "@media (max-width: 768px)": { flexWrap: "wrap" },
        }}
      >
        <Box
          component="iframe"
          src="https://www.youtube.com/embed/PaeHcrgGKAY?controls=0&rel=0&playsinline=1&autoplay=1&mute=1&loop=1&playlist=PaeHcrgGKAY"
          title="Background Video"
          aria-hidden="true"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: 0,
            pointerEvents: "none",
            zIndex: -1,
          }}
        />
        <Box sx={{ position: "relative", zIndex: 1, color: "#fff", textAlign: "center", px: 2 }}></Box>
      </Box>

      <Container maxWidth="md" sx={{ marginTop: -16 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: { xs: "0px 5px", sm: "0px 30px", md: "0px 30px" } }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            style={{ width: '100%' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                maxWidth: '700px',
              width: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: { xs: '20px 20px 0 0', md: '20px 20px 0 0' },
                padding: "0px 30px",
                position: 'relative',
                zIndex: 5,
                backdropFilter: 'saturate(180%) blur(10px)',
                WebkitBackdropFilter: 'saturate(180%) blur(10px)',
              }}
            >
              <Box component={motion.div} ref={ref} sx={{ position: 'absolute', top: { xs: -40, md: -95 }, right: 0 }}>
                <Box
                  component={motion.img}
                  src={Home_IMG.ball}
                  alt="Rotating sports ball graphic"
                  sx={{ width: { xs: '60px', sm: '80px', md: '150px' }, height: { xs: '60px', sm: '80px', md: '150px' } }}
                  style={{ rotate }}
                />
              </Box>

              <Box sx={{ textAlign: 'left', mt: -1 }}>
                <Typography variant="subtitle1" sx={{ display: 'inline-block', backgroundColor: COLORS.accentLime, color: COLORS.pureBlack, borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, px: 2, py: 0.5, mb: 2, mt: -15 }}>
                  About Us
                </Typography>

                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6 }}
                >
                  <Typography variant="h3" sx={{ mb: 2 }}>Where Every Child Discovers the Joy of Sport </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: COLORS.accentBlackTransparent }}>
                    At Grassroots Sports Education & Services, we believe every child deserves the opportunity to learn, play, and grow through sport. We partner with schools to deliver curriculum-aligned Sports & Physical Education programs that nurture physical literacy, character, confidence, and lifelong healthy habits. 
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: COLORS.accentBlackTransparent }}>
                    Our integrated approach combines multimedia curriculum, digital learning, qualified coaches, and innovative sports solutions to create meaningful experiences for every child while strengthening the grassroots sports ecosystem.
                  </Typography>
                </motion.div>

                <Box sx={{ mb: 3 ,alignItems: "center"}} >
                  <Grid container spacing={4} justifyContent="center" alignItems="center">
                    <Grid item xs="auto" md={4}><CustomTextButton label="Who We Are" href="/about" /></Grid>
                    <Grid item xs="auto" md={4}><CustomTextButton label="Our Mission" href="/about" /></Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: { xs: 2, md: 0 }, mt: 1 }}>
                  {[
                    { title: 'KG–Grade 12', subtitle: 'Curriculum-Aligned Sports & PE' },
                    { title: '10+ Services', subtitle: 'Complete Sports Education Solutions' },
                    { title: '10+ Sports', subtitle: 'Structured Learning Pathways' },
                  ].map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 50 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ delay: index * 0.2 }}
                      style={{ flex: '1 1 0', minWidth: 0 }}
                    >
                      <Box sx={{ textAlign: 'center', px: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{item.title}</Typography>
                        <Typography variant="subtitle1" sx={{ fontSize: '0.7rem', lineHeight: 1.3 }}>{item.subtitle}</Typography>
                      </Box>
                    </motion.div>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 6 }}>
                  {sportsIcons.map((SportIcon, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: COLORS.accentLime,
                        width: '20%',
                        height: { xs: 40, sm: 80, md: 80 },
                        borderRadius: '5px',
                        transform: 'skewX(-30deg)',
                      }}
                    >
                      <SportIcon sx={{ fontSize: { xs: 30, sm: 50, md: 50 }, color: COLORS.accentDarkGreen, stroke: '#000', strokeWidth: 0.5, fill: 'none', transform: 'skewX(30deg)' }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </motion.div>
        </Box>
      </Container>

      <Box sx={{ maxWidth: "100%", minHeight: "100px", backgroundColor: COLORS.accentLime, alignItems: "center", overflow: "hidden", position: "relative", display: "flex" }}>
        <motion.div
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 18, ease: "linear", repeat: Infinity }}
          style={{ display: "flex", gap: "2rem", width: "max-content" }}
        >
          {[...values, ...values].map((value, idx) => (
            <Typography
              key={`${value}-${idx}`}
              variant="h2"
              sx={{ m: 0, px: 2, whiteSpace: "nowrap", fontSize: { xs: "1.5rem", sm: "2rem", md: "4rem" }, fontFamily: "Big Shoulders Text, sans-serif", fontWeight: 600, color: idx % 2 === 0 ? COLORS.accentDarkGreen : COLORS.mediumBlack, textTransform: 'uppercase', lineHeight: '1.2em' }}
            >
              {value}
            </Typography>
          ))}
        </motion.div>
      </Box>
    </>
  );
};

export default BackgroundVideoSection;
